// Members & access screen — roster + whitelisted emails + invite-link manager.
import { getSession, type ProductRole, type Session, listAllowedEmails, addAllowedEmail, removeAllowedEmail, generateInviteLink, listInviteLinks, type InviteToken, approveAccessRequest, denyAccessRequest, subscribeAccessRequests } from '../auth';
import { toast } from '../components/toast';
import { openModal, closeModal } from '../components/modal';
import { listMembers, updateMemberRole, type MemberRow } from '../lib/repository';

let roster: MemberRow[] | null = null;
let allowed: string[] | null = null;
let invites: InviteToken[] | null = null;
let requests: Array<{ id: string; uid?: string; name: string; email: string; note?: string; status: string; createdAt?: number }> | null = null;
let domain: string | null = null;
let loading = false;
let _unsubReq: (() => void) | null = null;

const ROLES: ProductRole[] = ['owner', 'admin', 'teamLead', 'educator', 'salesAssociate', 'learner'];
function capitalizeRole(r: string): string {
  return r.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim();
}


function load(session: Session): void {
  if (loading) return;
  loading = true;
  Promise.all([
    listMembers().catch(() => []),
    listAllowedEmails(session.tenantId).catch(() => []),
    listInviteLinks(session.tenantId).catch(() => []),
  ])
    .then(([mem, allow, inv]) => {
      roster = mem; allowed = allow; invites = inv; loading = false; (window as any).__rerender?.();
    })
    .catch(() => { loading = false; });
  // Live notification feed for the user's own screen.
  _unsubReq ??= subscribeAccessRequests(session.tenantId, (rows) => {
    requests = rows;
    if ((location.hash || '').includes('/members')) (window as any).__rerender?.();
  });
}

export function renderMembers(): string {
  const session = getSession(); if (!session) return '<div class="card card-pad-lg empty"><h2>Not signed in</h2></div>';
  const isOwnerAdmin = session.role === 'owner' || session.role === 'admin';
  load(session);

  const memRows = (roster || []).map((m) => (
    '<div class="row" style="padding:9px 0;border-bottom:1px solid var(--rule)">' +
      '<div class="avatar av-c1">' + (m.name || m.email || '?').slice(0, 2).toUpperCase() + '</div>' +
      '<div style="flex:1;min-width:0"><div class="t-strong">' + (m.name || m.email) + '</div><div class="t-meta t-mono">' + (m.email || '') + '</div></div>' +
      (isOwnerAdmin
        ? '<select class="form-input role-sel" data-uid="' + m.id + '" style="max-width:160px;min-height:30px;padding:3px 6px;font-size:12px">' +
          ROLES.map((rr) => '<option value="' + rr + '" ' + (rr === m.role ? 'selected' : '') + '>' + capitalizeRole(rr) + '</option>').join('') + '</select>'
        : '<span class="pill pill-info">' + capitalizeRole(m.role) + '</span>') +
    '</div>'
  )).join('');

  const allowedRows = ((allowed || []).map((em) =>
    '<div class="row" style="padding:6px 0;border-bottom:1px solid var(--rule)">' +
      '<span class="t-mono t-meta" style="flex:1">' + em + '</span>' +
      (isOwnerAdmin ? '<button class="btn btn-ghost btn-sm" data-rm-email="' + em + '">Remove</button>' : '<span class="pill pill-success">whitelisted</span>') +
    '</div>'
  ).join('')) || '<div class="t-meta" style="padding:8px 0">No whitelisted emails yet — click “Invite link” below to onboard people.</div>';

  const invRows = (invites || []).map((i) => {
    const url = (typeof window !== 'undefined' ? window.location.origin : 'https://clientale.in') + '/#/invite/' + i.code;
    const status = i.used ? 'used' : (Date.now() > i.expiresAt ? 'expired' : 'active');
    const pillCls = status === 'active' ? 'pill-success' : 'pill-info';
    return (
      '<div class="row" style="padding:9px 0;border-bottom:1px solid var(--rule); gap:10px">' +
        '<span class="t-mono t-strong">' + i.code + '</span>' +
        '<span class="pill ' + pillCls + '">' + status + '</span>' +
        '<span class="pill pill-info">' + i.role + '</span>' +
        '<span class="t-meta" style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">(' + (i.email || 'anyone') + ')</span>' +
        (isOwnerAdmin && status === 'active' ? '<button class="btn btn-ghost btn-sm" data-copy-link="' + url + '">Copy link</button>' : '') +
      '</div>'
    );
  }).join('');

  const reqRows = (requests || []).map((q) => (
    '<div class="card card-pad-sm" style="margin-bottom:10px">' +
      '<div class="row"><div class="avatar av-c1">' + (q.name || '?').slice(0, 2).toUpperCase() + '</div><div style="flex:1;min-width:0">' +
      '<div class="t-strong">' + q.name + '</div><div class="t-meta t-mono">' + q.email + '</div>' +
      (q.note ? '<div class="t-meta">' + q.note + '</div>' : '') + '</div>' +
      '<span class="pill ' + (q.status === 'approved' ? 'pill-success' : q.status === 'denied' ? 'pill-warn' : 'pill-info') + '">' + q.status + '</span></div>' +
      (q.status === 'pending' && isOwnerAdmin ? '<div class="row" style="gap:8px;margin-top:10px;align-items:flex-end"><label style="display:flex; flex-direction:column; gap:4px;"><span class="t-eyebrow">Role</span><select class="form-input" data-req-role="' + q.id + '" style="min-height:34px; padding:4px 8px; font-size:13px;">' +
        ROLES.filter((r) => r !== 'owner').map((r) => '<option value="' + r + '">' + capitalizeRole(r) + '</option>').join('') + '</select></label>' +
        '<button class="btn btn-primary btn-sm" data-req-approve="' + q.id + '">Approve</button>' +
        '<button class="btn btn-ghost btn-sm" data-req-deny="' + q.id + '">Deny</button></div>' : '') +
    '</div>'
  )).join('');

  return (
    '<div class="screen-head"><div><div class="t-eyebrow">Workspace</div><h1 class="t-h1">Members & access</h1>' +
    '<p class="t-meta">Roster, whitelist of invited emails, shareable invite links, and pending access requests.</p></div>' +
    (isOwnerAdmin ? '<div style="display:flex;gap:8px"><button class="btn btn-accent btn-sm touch-target" data-action="new-invite">+ Invite link</button><button class="btn btn-ghost btn-sm touch-target" data-action="add-email">+ Whitelist email</button></div>' : '') + '</div>' +

    '<section class="card card-pad-lg" style="margin-top:16px"><div class="t-h2">Team</div>' +
    (memRows || '<div class="t-meta" style="padding:8px 0">No members yet.</div>') + '</section>' +

    (isOwnerAdmin
      ? '<section class="card card-pad-lg" style="margin-top:16px"><div class="t-h2">Whitelisted Google accounts</div>' +
        '<div class="t-meta" style="margin:6px 0 10px">Anyone whose Google account matches one of these emails can sign in. Approved access-requests are added automatically.</div>' +
        allowedRows + '</section>'
      : '') +

    '<section class="card card-pad-lg" style="margin-top:16px"><div class="t-h2">Invite links</div>' +
    '<div class="t-meta" style="margin:6px 0 10px">Each link is a one-time URL the recipient opens, signs in with Google, and gets the role you specified above. Set an optional email to lock the link to one account.</div>' +
    (invRows || '<div class="empty">No invite links yet — “+ Invite link” to generate one.</div>') + '</section>' +

    '<section class="card card-pad-lg" style="margin-top:16px"><div class="t-h2">Access requests</div>' +
    '<div class="t-meta" style="margin:6px 0 10px">People who aren’t on the whitelist yet can request access right from the login screen. Approve to add them to the whitelist automatically.</div>' +
    (reqRows || '<div class="empty">No requests yet.</div>') + '</section>'
  );
}

export function bindMembers(): void {
  document.addEventListener('click', async (e) => {
    const t = e.target as HTMLElement;
    const session = getSession(); if (!session) return;

    const approve = t.closest<HTMLElement>('[data-req-approve]');
    if (approve && approve.dataset.reqApprove) {
      const id = approve.dataset.reqApprove;
      const sel = document.querySelector<HTMLSelectElement>(`[data-req-role="${id}"]`);
      const role = (sel?.value || 'learner') as ProductRole;
      try { await approveAccessRequest(session.tenantId, id, role); toast('Approved as ' + role, 'success'); (window as any).__rerender?.(); }
      catch (err) { toast(err instanceof Error ? err.message : 'Could not approve', 'warn'); }
      return;
    }
    const deny = t.closest<HTMLElement>('[data-req-deny]');
    if (deny && deny.dataset.reqDeny) {
      try { await denyAccessRequest(session.tenantId, deny.dataset.reqDeny); toast('Request denied', 'warn'); (window as any).__rerender?.(); }
      catch (err) { toast(err instanceof Error ? err.message : 'Could not deny', 'warn'); }
      return;
    }
    const rmEmail = t.closest<HTMLButtonElement>('[data-rm-email]');
    if (rmEmail && rmEmail.dataset.rmEmail) {
      try { await removeAllowedEmail(session.tenantId, rmEmail.dataset.rmEmail); toast('Removed from whitelist', 'success'); (window as any).__rerender?.(); }
      catch (err) { toast(err instanceof Error ? err.message : 'Could not remove', 'warn'); }
      return;
    }
    const copy = t.closest<HTMLButtonElement>('[data-copy-link]');
    if (copy && copy.dataset.copyLink) {
      try { await navigator.clipboard?.writeText(copy.dataset.copyLink); toast('Invite link copied', 'success'); }
      catch { toast(copy.dataset.copyLink, 'success'); }
      return;
    }
    if (t.closest('[data-action="new-invite"]') && !document.getElementById('invite-form')) openInviteForm();
    if (t.closest('[data-action="add-email"]') && !document.getElementById('email-form')) openEmailForm();
  });

  document.addEventListener('change', async (e) => {
    const sel = (e.target as HTMLElement).closest<HTMLSelectElement>('.role-sel');
    if (!sel || !sel.dataset.uid) return;
    try { await updateMemberRole(sel.dataset.uid, sel.value); toast('Role updated to ' + sel.value, 'success'); }
    catch (err) { toast(err instanceof Error ? err.message : 'Role update failed', 'warn'); }
  });

  document.addEventListener('submit', async (e) => {
    const form = (e.target as HTMLElement).closest<HTMLFormElement>('#invite-form');
    if (form) {
      e.preventDefault();
      const session = getSession(); if (!session) return;
      const fd = new FormData(form);
      const role = String(fd.get('role') || 'learner') as ProductRole;
      const note = String(fd.get('note') || '').trim();
      const email = String(fd.get('email') || '').trim();
      const hours = Number(fd.get('hours') || 168);
      try {
        const r = await generateInviteLink(session.tenantId, { role, note, email, hours });
        form.remove();
        toast('Invite link generated for role: ' + role + ' · link: ' + r.url, 'success');
        (window as any).__rerender?.();
      } catch (err) { toast(err instanceof Error ? err.message : 'Could not create invite', 'warn'); }
      return;
    }
    const emailForm = (e.target as HTMLElement).closest<HTMLFormElement>('#email-form');
    if (emailForm) {
      e.preventDefault();
      const session = getSession(); if (!session) return;
      const fd = new FormData(emailForm);
      const em = String(fd.get('email') || '').trim();
      if (!em) { toast('Email required', 'warn'); return; }
      try { await addAllowedEmail(session.tenantId, em); toast(em + ' whitelisted — they can sign in now', 'success'); emailForm.remove(); (window as any).__rerender?.(); }
      catch (err) { toast(err instanceof Error ? err.message : 'Could not add', 'warn'); }
    }
  });
}

function openInviteForm(): void {
  openModal({
    id: 'invite',
    title: 'New invite link',
    subtitle: 'One link, one role. Optionally lock to a single email.',
    width: 500,
    html: '<form id="invite-form">' +
      '<div class="stack-tight">' +
        '<label class="t-eyebrow">Role<select name="role" class="form-input">' + ROLES.filter((rr) => rr !== 'owner').map((rr) => '<option value="' + rr + '" ' + (rr === 'learner' ? 'selected' : '') + '>' + capitalizeRole(rr) + '</option>').join('') + '</select></label>' +
        '<label class="t-eyebrow">Lock to email (blank = anyone with the link)<input name="email" type="email" class="form-input" placeholder="optional"></label>' +
        '<label class="t-eyebrow">Expires in (hours)<input type="number" name="hours" value="168" class="form-input"/></label>' +
        '<label class="t-eyebrow">Note<input name="note" class="form-input" placeholder="e.g. Sep batch educator"/></label>' +
      '</div>' +
      '<div class="row" style="margin-top:20px;gap:10px;justify-content:flex-end">' +
        '<button class="btn btn-ghost" type="button" data-close-invite>Cancel</button>' +
        '<button class="btn btn-primary" type="submit">Generate link</button>' +
      '</div>' +
    '</form>',
  }, (panel) => {
    panel.querySelectorAll('[data-close-invite]').forEach((b) => b.addEventListener('click', () => closeModal('invite')));
  });
}

function openEmailForm(): void {
  openModal({
    id: 'email',
    title: 'Whitelist a Google account',
    subtitle: 'An exact email — that Google account can sign in immediately.',
    width: 480,
    html: '<form id="email-form">' +
      '<div class="stack-tight"><label class="t-eyebrow">Email<input name="email" type="email" required class="form-input" placeholder="alice@example.com"></label></div>' +
      '<div class="row" style="margin-top:20px;gap:10px;justify-content:flex-end">' +
        '<button class="btn btn-ghost" type="button" data-close-email>Cancel</button>' +
        '<button class="btn btn-primary" type="submit">Whitelist</button>' +
      '</div>' +
    '</form>',
  }, (panel) => {
    panel.querySelectorAll('[data-close-email]').forEach((b) => b.addEventListener('click', () => closeModal('email')));
  });
}
